import type { ReactNode } from 'react';
import { AuthLayout } from './AuthLayout';

interface SignUpFormFieldsProps {
  email: string;
  setEmail: (v: string) => void;
  password: string;
  setPassword: (v: string) => void;
  confirmPassword: string;
  setConfirmPassword: (v: string) => void;
  error: string;
  loading: boolean;
  onSubmit: (e: React.FormEvent) => void;
  title: string;
  subtitle: string;
  submitLabel: string;
  submittingLabel: string;
  footer: ReactNode;
}

export function SignUpFormFields({
  email,
  setEmail,
  password,
  setPassword,
  confirmPassword,
  setConfirmPassword,
  error,
  loading,
  onSubmit,
  title,
  subtitle,
  submitLabel,
  submittingLabel,
  footer,
}: SignUpFormFieldsProps) {
  return (
    <AuthLayout title={title} subtitle={subtitle}>
      <form onSubmit={onSubmit} className="space-y-6">
        {error && (
          <div className="bg-red-500/10 border border-red-500/20 rounded-lg p-3">
            <p className="text-red-400 text-sm">{error}</p>
          </div>
        )}

        <div>
          <label htmlFor="email" className="block text-sm font-medium text-foreground mb-2">
            メールアドレス
          </label>
          <input
            id="email"
            type="email"
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            required
            className="w-full px-4 py-3 bg-muted/50 border border-input rounded-lg text-foreground placeholder-muted-foreground focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-transparent"
            placeholder="example@email.com"
          />
        </div>

        <div>
          <label htmlFor="password" className="block text-sm font-medium text-foreground mb-2">
            パスワード
          </label>
          <input
            id="password"
            type="password"
            value={password}
            onChange={(e) => setPassword(e.target.value)}
            required
            className="w-full px-4 py-3 bg-muted/50 border border-input rounded-lg text-foreground placeholder-muted-foreground focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-transparent"
            placeholder="パスワードを入力"
          />
        </div>

        <div>
          <label
            htmlFor="confirmPassword"
            className="block text-sm font-medium text-foreground mb-2"
          >
            パスワード（確認）
          </label>
          <input
            id="confirmPassword"
            type="password"
            value={confirmPassword}
            onChange={(e) => setConfirmPassword(e.target.value)}
            required
            className="w-full px-4 py-3 bg-muted/50 border border-input rounded-lg text-foreground placeholder-muted-foreground focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-transparent"
            placeholder="パスワードを再入力"
          />
        </div>

        <button
          type="submit"
          disabled={loading}
          className="w-full py-3 px-4 bg-primary-500 hover:bg-primary-600 text-white font-medium rounded-lg transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
        >
          {loading ? submittingLabel : submitLabel}
        </button>
      </form>

      {footer}
    </AuthLayout>
  );
}

interface ConfirmCodeFormProps {
  email: string;
  confirmationCode: string;
  setConfirmationCode: (v: string) => void;
  error: string;
  loading: boolean;
  onSubmit: (e: React.FormEvent) => void;
}

export function ConfirmCodeForm({
  email,
  confirmationCode,
  setConfirmationCode,
  error,
  loading,
  onSubmit,
}: ConfirmCodeFormProps) {
  return (
    <AuthLayout title="メール確認" subtitle="確認コードをメールで送信しました">
      <form onSubmit={onSubmit} className="space-y-6">
        {error && (
          <div className="bg-red-500/10 border border-red-500/20 rounded-lg p-3">
            <p className="text-red-400 text-sm">{error}</p>
          </div>
        )}

        <div className="bg-blue-500/10 border border-blue-500/20 rounded-lg p-3">
          <p className="text-blue-400 text-sm">{email} に確認コードを送信しました。</p>
        </div>

        <div>
          <label htmlFor="code" className="block text-sm font-medium text-foreground mb-2">
            確認コード
          </label>
          <input
            id="code"
            type="text"
            value={confirmationCode}
            onChange={(e) => setConfirmationCode(e.target.value)}
            required
            className="w-full px-4 py-3 bg-muted/50 border border-input rounded-lg text-foreground placeholder-muted-foreground focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-transparent"
            placeholder="123456"
          />
        </div>

        <button
          type="submit"
          disabled={loading}
          className="w-full py-3 px-4 bg-primary-500 hover:bg-primary-600 text-white font-medium rounded-lg transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
        >
          {loading ? '確認中...' : '確認'}
        </button>
      </form>
    </AuthLayout>
  );
}

interface SignUpCompleteProps {
  message: string;
  buttonLabel: string;
  onNavigate: () => void;
}

export function SignUpComplete({ message, buttonLabel, onNavigate }: SignUpCompleteProps) {
  return (
    <AuthLayout title="登録完了" subtitle="アカウントの作成が完了しました">
      <div className="space-y-6">
        <div className="bg-green-500/10 border border-green-500/20 rounded-lg p-4">
          <p className="text-green-400 text-sm text-center">
            メールアドレスの確認が完了しました。
            <br />
            {message}
          </p>
        </div>

        <button
          type="button"
          onClick={onNavigate}
          className="w-full py-3 px-4 bg-primary-500 hover:bg-primary-600 text-white font-medium rounded-lg transition-colors"
        >
          {buttonLabel}
        </button>
      </div>
    </AuthLayout>
  );
}
